'use strict';

import './components/index';

import { showEmptyResults } from '@core/utils/empty-results';
import {
    updateFiltersStateHistoryPage,
    updateFiltersStateHistoryPageReport,
} from '@core/utils/history-state';
import {
    getCurrentSearcherObj,
    allowTriggerSearchQueryEvent,
    isAIActivated,
} from '@core/utils/searcher';
import { isAIServerMaintenance } from '@components/modals/AIServerOnMaintenanceModal/utils';
import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import {
    UPDATE_LANGUAGE_EVENT,
    SEARCH_IMG_RESULT_EVENT,
    TRIGGER_SEARCH_QUERY_EVENT,
    SEARCH_REPORTS_RESULT_EVENT,
    SEARCH_REPORT_DETAILS_RESULT_EVENT,
    SEARCH_IMG_REPORTS_RESULT_EVENT,
    TRIGGER_HIDE_LOADING_COMPONENT_EVENT,
    TRIGGER_UPDATE_PER_PAGE_EVENT,
    TRIGGER_SHOW_LOADING_COMPONENT_EVENT,
} from '@core/constants/EVENTS';
import { SEARCH_IMG_QUERY_PARAMS } from '@core/constants/API';
import { ROUTES } from '@core/constants/ROUTES';
import { MIN_ACTIVES_HEIGHT, DEFAULT_LOAD_PREVIOUS_PAGES } from '@core/constants/DEFAULT';
import { REPORT_TYPE } from '@core/constants/REPORTS';
import { showSpinnerButton, isDetailsView } from '@core/utils/utils';
import { SEARCH_HANDLE_BY_USER, SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { getStoreConfiguration, updateStoreFiltersState } from '@core/utils/store';
import {
    STORE_SEARCHER_FILTERS,
    WESOG_STORE_CONFIGURATION_PARAMS,
} from '@core/constants/STORE';
import {
    DATA_ID_ATTRIBUTE,
    DETAILS_VIEW_ATTRIBUTE_IMG,
} from '@core/constants/ATTRIBUTES';
import { globalSharedStyles, typographySharedStyles } from '@core/styles';
import { SEARCH_LIGHTBOX_RESULT_EVENT } from '@core/constants/LIGHTBOX';

import {
    RESULTS_GRID_LAYOUT_WEB_COMPONENT,
    RESULTS_GRID_LAYOUT_ATTRIBUTES,
    CUSTOM_RESULTS_GRID_LOADING_SPINNER_WEB_COMPONENT,
} from './CONSTANTS';
import htmlContent from './ResultsGridLayout.html';
import cssContent from './ResultsGridLayout.css';
import {
    isAIActivatedAndNotDetailsView,
    checkEntries,
    createIntersectionObserver,
    renderGridElements,
    getSelectedElement,
    showLoadingSpinner,
    hideLoadingSpinner,
    getPaginationConfig,
    getPerPageConfig,
    calculateTotalPagination,
    dispatchSearchQueryEvent,
    scrollToTop,
    scrollToElement,
    fetchMoreContent,
    fetchLessContent,
    showAIServerOffline,
} from './utils';

/**
 * ResultsGridLayout component. This component is responsible for rendering the search results
 * in a grid layout with pagination and infinite scroll capabilities.
 *
 * @class ResultsGridLayout
 * @extends {HTMLElement}
 */
class ResultsGridLayout extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            typographySharedStyles,
            styleSheet,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize component properties
        this._isLoading = false;
        this._currentView = ROUTES.RESULTS;
        this._observer = null;
        this._secondObserver = null;
        this._timesQuery = 0;
        this._isDifferentReportType = false;
        this._searchImgsResults = !!this.getAttribute(
            RESULTS_GRID_LAYOUT_ATTRIBUTES.SEARCH_IMGS_RESULTS,
        );
        this._searchReportsResults = !!this.getAttribute(
            RESULTS_GRID_LAYOUT_ATTRIBUTES.SEARCH_REPORTS_RESULTS,
        );
        this._currentReportType = '';
        this._totalElements = 0;
        this._unauthorized = false;
        this._currentPage = 0;
        this._firstRender = true;
        this._totalPagination = 0;
        this._avoidRightPaginationButtons = [
            ROUTES.REPORTS_LIST,
            ROUTES.LIGHT_BOX,
            ROUTES.RESULTS,
        ];
        this._fistQuery = true;
        this._loadPreviousElements = 0;
        this._loadPreviousElementsButtonText = '';
        this._loadPreviousElementsFirstElementId = null;
        this._storeAIElements = [];
        this._AIqueryTimes = 0;
        this._AIQueryFirstPage = 0;
        this._AIQueryRecursive = false;
        this._lastSelectedElementId = null;

        // Cache DOM references
        this._container = null;
        this._loadingSpinner = null;
        this._loadPreviousElementsButton = null;
        this._paginationContainer = null;
        this._paginationValue = null;
        this._leftButton = null;
        this._rightButton = null;
        this._doubleLeftButton = null;
        this._doubleRightButton = null;
        this._trigger = null;
        this._secondTrigger = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleLoadPreviousElementsClickBound =
            this._handleLoadPreviousElementsClick.bind(this);
        this._handleUpdateLanguageBound = this._handleUpdateLanguage.bind(this);
        this._handleSearchImgResultBound = this._handleSearchImgResult.bind(this);
        this._handleSearchImgReportsResultBound =
            this._handleSearchImgReportsResult.bind(this);
        this._handleSearchReportsResultBound = this._handleSearchReportsResult.bind(this);
        this._handleSearchReportDetailsResultBound =
            this._handleSearchReportDetailsResult.bind(this);
        this._handleSearchLightboxResultBound =
            this._handleSearchLightboxResult.bind(this);
        this._handleTriggerHideLoadingComponentBound =
            this._handleTriggerHideLoadingComponent.bind(this);
        this._handleTriggerUpdatePerPageBound =
            this._handleTriggerUpdatePerPage.bind(this);
        this._handleTriggerSearchQueryBound = this._handleTriggerSearchQuery.bind(this);
        this._handlePaginationLeftClickBound = this._handlePaginationLeftClick.bind(this);
        this._handlePaginationRightClickBound =
            this._handlePaginationRightClick.bind(this);
        this._handlePaginationDoubleLeftClickBound =
            this._handlePaginationDoubleLeftClick.bind(this);
        this._handlePaginationDoubleRightClickBound =
            this._handlePaginationDoubleRightClick.bind(this);
        this._handleTriggerShowLoadingComponentBound =
            this._handleTriggerShowLoadingComponent.bind(this);
    }

    /**
     * Called when the element is connected to the DOM.
     * @returns {void}
     */
    connectedCallback() {
        this._cacheDOMReferences();
        this._addEventListeners();
        this._handleLoadPreviousElements();
        this._showLoadingSpinner();
    }

    /**
     * Called when the element is disconnected from the DOM.
     * @returns {void}
     */
    disconnectedCallback() {
        this._removeEventListeners();
        this._cleanupObservers();
        this._clearTimeouts();
    }

    /**
     * Cache DOM references for better performance.
     * @returns {void}
     * @private
     */
    _cacheDOMReferences() {
        this._container = this.shadowRoot.querySelector('[data-grid-container]');
        this._loadingSpinner = this.shadowRoot.querySelector(
            CUSTOM_RESULTS_GRID_LOADING_SPINNER_WEB_COMPONENT,
        );
        this._loadPreviousElementsButton = this.shadowRoot.querySelector(
            '[data-load-previous-elements]',
        );
        this._paginationContainer = this.shadowRoot.querySelector(
            '.results-grid-pagination-container',
        );
        this._paginationValue = this.shadowRoot.querySelector('[data-pagination-value]');
        this._leftButton = this.shadowRoot.querySelector('[data-pagination-left-button]');
        this._rightButton = this.shadowRoot.querySelector(
            '[data-pagination-right-button]',
        );
        this._doubleLeftButton = this.shadowRoot.querySelector(
            '[data-pagination-double-left-button]',
        );
        this._doubleRightButton = this.shadowRoot.querySelector(
            '[data-pagination-double-right-button]',
        );
        this._trigger = this.shadowRoot.querySelector('#trigger');
        this._secondTrigger = this.shadowRoot.querySelector('#second-trigger');
    }

    /**
     * Add event listeners to the component.
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(UPDATE_LANGUAGE_EVENT, this._handleUpdateLanguageBound);
        document.addEventListener(
            SEARCH_IMG_RESULT_EVENT,
            this._handleSearchImgResultBound,
        );
        document.addEventListener(
            SEARCH_IMG_REPORTS_RESULT_EVENT,
            this._handleSearchImgReportsResultBound,
        );
        document.addEventListener(
            SEARCH_REPORTS_RESULT_EVENT,
            this._handleSearchReportsResultBound,
        );
        document.addEventListener(
            SEARCH_REPORT_DETAILS_RESULT_EVENT,
            this._handleSearchReportDetailsResultBound,
        );
        document.addEventListener(
            SEARCH_LIGHTBOX_RESULT_EVENT,
            this._handleSearchLightboxResultBound,
        );
        document.addEventListener(
            TRIGGER_SHOW_LOADING_COMPONENT_EVENT,
            this._handleTriggerShowLoadingComponentBound,
        );
        document.addEventListener(
            TRIGGER_HIDE_LOADING_COMPONENT_EVENT,
            this._handleTriggerHideLoadingComponentBound,
        );
        document.addEventListener(
            TRIGGER_UPDATE_PER_PAGE_EVENT,
            this._handleTriggerUpdatePerPageBound,
        );
        document.addEventListener(
            TRIGGER_SEARCH_QUERY_EVENT,
            this._handleTriggerSearchQueryBound,
        );
    }

    /**
     * Remove event listeners from the component.
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleUpdateLanguageBound,
        );
        document.removeEventListener(
            SEARCH_IMG_RESULT_EVENT,
            this._handleSearchImgResultBound,
        );
        document.removeEventListener(
            SEARCH_IMG_REPORTS_RESULT_EVENT,
            this._handleSearchImgReportsResultBound,
        );
        document.removeEventListener(
            SEARCH_REPORTS_RESULT_EVENT,
            this._handleSearchReportsResultBound,
        );
        document.removeEventListener(
            SEARCH_REPORT_DETAILS_RESULT_EVENT,
            this._handleSearchReportDetailsResultBound,
        );
        document.removeEventListener(
            SEARCH_LIGHTBOX_RESULT_EVENT,
            this._handleSearchLightboxResultBound,
        );
        document.removeEventListener(
            TRIGGER_SHOW_LOADING_COMPONENT_EVENT,
            this._handleTriggerShowLoadingComponentBound,
        );
        document.removeEventListener(
            TRIGGER_HIDE_LOADING_COMPONENT_EVENT,
            this._handleTriggerHideLoadingComponentBound,
        );
        document.removeEventListener(
            TRIGGER_UPDATE_PER_PAGE_EVENT,
            this._handleTriggerUpdatePerPageBound,
        );
        document.removeEventListener(
            TRIGGER_SEARCH_QUERY_EVENT,
            this._handleTriggerSearchQueryBound,
        );
    }

    /**
     * Show the custom loading spinner for the results grid layout.
     * @returns {void}
     * @private
     */
    _showLoadingSpinner() {
        if (this._loadingSpinner && isDetailsView()) {
            this._loadingSpinner.style.display = 'flex';
        }
    }

    /**
     * Clean up intersection observers.
     * @returns {void}
     * @private
     */
    _cleanupObservers() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
        if (this._secondObserver) {
            this._secondObserver.disconnect();
            this._secondObserver = null;
        }
    }

    /**
     * Clear any active timeouts.
     * @returns {void}
     * @private
     */
    _clearTimeouts() {
        // Clear any timeouts if they exist
        if (this._moveToSelectedElementTimeout) {
            clearTimeout(this._moveToSelectedElementTimeout);
            this._moveToSelectedElementTimeout = null;
        }
        if (this._scrollToElementTimeout) {
            clearTimeout(this._scrollToElementTimeout);
            this._scrollToElementTimeout = null;
        }
    }

    /**
     * Handle the load previous elements button.
     * @returns {void}
     * @private
     */
    _handleLoadPreviousElements() {
        if (!this._loadPreviousElementsButton) return;

        this._loadPreviousElementsButton.addEventListener(
            'click',
            this._handleLoadPreviousElementsClickBound,
        );
    }

    /**
     * Handle load previous elements button click.
     * @returns {void}
     * @private
     */
    _handleLoadPreviousElementsClick() {
        if (this._isLoading) return;

        const firstElement = this._container?.firstElementChild;
        if (!firstElement) return;

        this._loadPreviousElementsFirstElementId =
            firstElement.getAttribute(DATA_ID_ATTRIBUTE);
        const firstElementPage = JSON.parse(
            firstElement.getAttribute(DETAILS_VIEW_ATTRIBUTE_IMG),
        )?.current_page;

        this._loadPreviousElements = firstElementPage - DEFAULT_LOAD_PREVIOUS_PAGES;
        this._loadPreviousElements =
            this._loadPreviousElements < 0 ? 0 : this._loadPreviousElements;

        this._loadPreviousElementsButtonText = this._loadPreviousElementsButton.innerHTML;
        showSpinnerButton(this._loadPreviousElementsButton);
        this._handleTriggerShowLoadingComponent();

        dispatchSearchQueryEvent({
            [SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]: true,
            [SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS_PAGE]: this._loadPreviousElements,
            [SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS_LAST_PAGE]: firstElementPage,
        });
    }

    /**
     * Translate the component elements.
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Handle language update event.
     * @returns {void}
     * @private
     */
    _handleUpdateLanguage() {
        this._handleTranslationHelper();
    }

    /**
     * Handle search image result event.
     * @param {CustomEvent} event - The search image result event.
     * @returns {void}
     * @private
     */
    _handleSearchImgResult(event) {
        this._currentView = isDetailsView() ? ROUTES.DETAILS : ROUTES.RESULTS;
        this._unauthorized = event.detail.unauthorized || false;

        const urlParams = new URLSearchParams(window.location.search);
        const configValue = getStoreConfiguration();

        if (!this._searchImgsResults) {
            return;
        }

        const currentFiltersStateHistory = getCurrentSearcherObj();

        if (event.detail.page === 0) {
            this._totalElements = event.detail.totalImgs;
            scrollToTop();

            if (currentFiltersStateHistory) {
                currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_IMGS] =
                    event.detail.totalImgs;
                updateStoreFiltersState(
                    urlParams.get(SEARCHER_TYPES.SEARCHER_ID),
                    currentFiltersStateHistory,
                );
            }
        } else {
            if (!currentFiltersStateHistory) return;
            this._totalElements =
                currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_IMGS];
        }

        const paginationConfigValue =
            configValue[WESOG_STORE_CONFIGURATION_PARAMS.PAGINATION];
        if (paginationConfigValue && this._doubleRightButton) {
            this._doubleRightButton.style.display = 'flex';
        }

        if (
            isAIActivatedAndNotDetailsView(isAIActivated(), this._currentView) &&
            !event.detail.errorMessages
        ) {
            this._storeAIElements = this._storeAIElements.concat(event.detail.imgs);
            const perPageConfigValue =
                configValue[WESOG_STORE_CONFIGURATION_PARAMS.PER_PAGE];

            if (
                this._storeAIElements.length < perPageConfigValue &&
                this._AIqueryTimes < 3
            ) {
                this._currentPage = event.detail.page;
                this._AIQueryRecursive = true;

                if (!this._AIqueryTimes) {
                    this._AIQueryFirstPage = event.detail.page;
                }
                this._AIqueryTimes += 1;

                fetchMoreContent(this._currentPage, this._searchReportsResults, {
                    [SEARCH_IMG_QUERY_PARAMS.RECURSIVE_AI_SEARCH]: true,
                });
            } else {
                dispatchSearchQueryEvent(
                    {
                        [SEARCH_IMG_QUERY_PARAMS.RECURSIVE_AI_SEARCH]: false,
                    },
                    true,
                );
                this._handleEventListeners(
                    this._storeAIElements,
                    event.detail.page,
                    event.detail.loadPrevious,
                );

                this._storeAIElements = [];
                this._AIqueryTimes = 0;
            }
        } else {
            this._handleEventListeners(
                event.detail.imgs,
                event.detail.page,
                event.detail.loadPrevious,
            );
        }
    }

    /**
     * Handle search image reports result event.
     * @param {CustomEvent} event - The search image reports result event.
     * @returns {void}
     * @private
     */
    _handleSearchImgReportsResult(event) {
        this._currentView = isDetailsView() ? ROUTES.DETAILS : ROUTES.REPORTS_LIST;
        this._currentReportType = 'search-imgs-reports';

        if (this._searchReportsResults) {
            const currentFiltersStateHistory = getCurrentSearcherObj();
            const urlParams = new URLSearchParams(window.location.search);

            if (event.detail.page === 0) {
                this._totalElements = event.detail.totalReports;
                scrollToTop();

                if (currentFiltersStateHistory) {
                    currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_REPORTS] =
                        event.detail.totalReports;
                    updateStoreFiltersState(
                        urlParams.get(SEARCHER_TYPES.SEARCHER_ID),
                        currentFiltersStateHistory,
                    );
                }
            } else {
                if (!currentFiltersStateHistory) return;
                this._totalElements =
                    currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_REPORTS];
            }

            const paginationConfigValue =
                getStoreConfiguration()[WESOG_STORE_CONFIGURATION_PARAMS.PAGINATION];
            if (paginationConfigValue && this._doubleRightButton) {
                this._doubleRightButton.style.display = 'flex';
            }

            this._handleEventListeners(
                event.detail.reports,
                event.detail.page,
                event.detail.loadPrevious,
            );
        }
    }

    /**
     * Handle search reports result event.
     * @param {CustomEvent} event - The search reports result event.
     * @returns {void}
     * @private
     */
    _handleSearchReportsResult(event) {
        this._currentView = ROUTES.REPORTS_LIST;
        this._currentReportType = event.detail.reportFilter || REPORT_TYPE.ED_MOST_VIEWED;

        if (!this._searchImgsResults) return;

        if (event.detail.page === 0) {
            scrollToTop();
        }

        this._handleEventListeners(
            event.detail.reports,
            event.detail.page,
            event.detail.loadPrevious,
        );
    }

    /**
     * Handle search report details result event.
     * @param {CustomEvent} event - The search report details result event.
     * @returns {void}
     * @private
     */
    _handleSearchReportDetailsResult(event) {
        this._currentView = ROUTES.REPORT_DETAILS;

        if (!this._searchImgsResults) return;

        const currentFiltersStateHistory = getCurrentSearcherObj();
        const urlParams = new URLSearchParams(window.location.search);

        if (event.detail.page === 0) {
            this._totalElements = event.detail.totalImgs;
            scrollToTop();

            if (currentFiltersStateHistory) {
                currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_IMGS] =
                    event.detail.totalImgs;
                updateStoreFiltersState(
                    urlParams.get(SEARCHER_TYPES.SEARCHER_ID),
                    currentFiltersStateHistory,
                );
            }
        } else {
            if (!currentFiltersStateHistory) return;
            this._totalElements =
                currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_IMGS];
        }

        const paginationConfigValue =
            getStoreConfiguration()[WESOG_STORE_CONFIGURATION_PARAMS.PAGINATION];
        if (paginationConfigValue && this._doubleRightButton) {
            this._doubleRightButton.style.display = 'flex';
        }

        this._handleEventListeners(
            event.detail.imgs,
            event.detail.page,
            event.detail.loadPrevious,
        );
    }

    /**
     * Handle search lightbox result event.
     * @param {CustomEvent} event - The search lightbox result event.
     * @returns {void}
     * @private
     */
    _handleSearchLightboxResult(event) {
        this._currentView = ROUTES.LIGHT_BOX;

        if (event.detail.page === 0) {
            scrollToTop();
        }

        this._handleEventListeners(
            event.detail.lightbox,
            event.detail.page,
            event.detail.loadPrevious,
        );
    }

    /**
     * Handle trigger hide loading component event.
     * @returns {void}
     * @private
     */
    _handleTriggerHideLoadingComponent() {
        this._isLoading = false;
        hideLoadingSpinner(this.shadowRoot);
    }

    /**
     * Handle trigger show loading component event.
     * @param {CustomEvent} event - The trigger show loading component event.
     * @returns {void}
     * @private
     */
    _handleTriggerShowLoadingComponent(event) {
        this._isLoading = true;
        this._unauthorized = event?.detail?.unauthorized || false;
        showLoadingSpinner({
            shadowRoot: this.shadowRoot,
            unauthorized: this._unauthorized,
            isDetailsView: this._currentView === ROUTES.DETAILS,
        });
    }

    /**
     * Handle trigger update per page event.
     * @returns {void}
     * @private
     */
    _handleTriggerUpdatePerPage() {
        if (this._searchReportsResults) {
            updateFiltersStateHistoryPageReport();
        } else {
            updateFiltersStateHistoryPage();
        }
    }

    /**
     * Handle trigger search query event.
     * @param {CustomEvent} event - The trigger search query event.
     * @returns {void}
     * @private
     */
    _handleTriggerSearchQuery(event) {
        if (this._currentView === ROUTES.DETAILS) return;

        let avoidTriggerQuery = !allowTriggerSearchQueryEvent();
        if (
            event.detail &&
            typeof event.detail === 'object' &&
            event.detail[SEARCH_HANDLE_BY_USER]
        ) {
            avoidTriggerQuery = false;
        }

        if (event.cancelable || avoidTriggerQuery) {
            return;
        }

        if (
            event.detail &&
            typeof event.detail === 'object' &&
            event.detail.page !== undefined &&
            event.detail.page === 0
        ) {
            this._removeContent();
        }
        this._showLoading();
    }

    /**
     * Handle event listeners for search results.
     * @param {Array<object>} data - The data to render.
     * @param {number} page - The page of the data.
     * @param {boolean} loadPrevious - True if the data is loaded from the previous pages.
     * @returns {void}
     * @private
     */
    _handleEventListeners(data, page, loadPrevious) {
        this._handleTriggerHideLoadingComponent();
        this._unauthorized = false;
        this._currentPage = page;

        if (loadPrevious) {
            this._renderPreviousGrid(data);
            return;
        }

        if (
            (this._currentPage === 0 || this._AIQueryFirstPage === 0) &&
            (!data || (data && data.length === 0))
        ) {
            if (isAIActivated() && isAIServerMaintenance()) {
                showAIServerOffline(this.shadowRoot, '[data-grid-container]');
            } else {
                showEmptyResults(this.shadowRoot, '[data-grid-container]');
            }
            return;
        }

        this._renderGrid(data);
    }

    /**
     * Render the data in the grid.
     * @param {Array} data - The data to render.
     * @returns {void}
     * @private
     */
    _renderGrid(data) {
        const paginationConfigValue = getPaginationConfig();
        const perPageConfigValue = getPerPageConfig();

        if (this._currentPage === 0 || !paginationConfigValue) {
            this._removeContent();
        }

        if (data && data.length === 0) {
            this._timesQuery += 1;
        }

        renderGridElements(
            data,
            this._container,
            this._currentView,
            this._currentReportType,
            this.getAttribute(RESULTS_GRID_LAYOUT_ATTRIBUTES.TITLE),
            this._searchImgsResults,
            MIN_ACTIVES_HEIGHT,
        );

        if (this._fistQuery) {
            const shouldShowButton = this._AIQueryRecursive
                ? paginationConfigValue && this._AIQueryFirstPage > 0
                : paginationConfigValue && this._currentPage > 0;

            if (this._loadPreviousElementsButton) {
                this._loadPreviousElementsButton.style.display = shouldShowButton
                    ? 'flex'
                    : 'none';
            }
            this._AIQueryRecursive = false;

            if (paginationConfigValue) {
                this._moveToSelectedElement();
            }
        } else {
            if (
                paginationConfigValue &&
                (!this._currentPage ||
                    (!this._AIQueryFirstPage && this._AIQueryRecursive))
            ) {
                if (this._loadPreviousElementsButton) {
                    this._loadPreviousElementsButton.style.display = 'none';
                }
            }
        }

        this._setupInfiniteScroll(data, paginationConfigValue, perPageConfigValue);
        this._fistQuery = false;
    }

    /**
     * Setup infinite scroll functionality.
     * @param {Array} data - The data array.
     * @param {boolean} paginationConfigValue - Whether pagination is enabled.
     * @param {number} perPageConfigValue - The per page configuration.
     * @returns {void}
     * @private
     */
    _setupInfiniteScroll(data, paginationConfigValue, perPageConfigValue) {
        const loadMoreEntries = (entries) => {
            if (
                checkEntries(entries) &&
                !this._isLoading &&
                this._container.innerHTML !== '' &&
                this._currentView !== ROUTES.DETAILS &&
                !this._fistQuery
            ) {
                this._handleTriggerShowLoadingComponent();
                fetchMoreContent(this._currentPage, this._searchReportsResults);
            }
        };

        if (paginationConfigValue) {
            if (this._paginationContainer) {
                this._paginationContainer.style.display = 'none';
            }

            if (
                !this._observer &&
                this._currentView !== ROUTES.DETAILS &&
                this._trigger
            ) {
                this._observer = createIntersectionObserver(loadMoreEntries, '1200px');
                this._observer.observe(this._trigger);
            } else if (this._observer && data.length < perPageConfigValue) {
                this._observer.disconnect();
                this._observer = null;
            }

            if (
                !this._secondObserver &&
                this._currentView !== ROUTES.DETAILS &&
                this._secondTrigger
            ) {
                this._secondObserver = createIntersectionObserver(loadMoreEntries, '0px');
                this._secondObserver.observe(this._secondTrigger);
            } else if (this._secondObserver && data.length < perPageConfigValue) {
                this._secondObserver.disconnect();
                this._secondObserver = null;
            }
        } else if (this._currentView !== ROUTES.DETAILS) {
            if (this._paginationContainer) {
                this._paginationContainer.style.display = 'flex';
            }

            this._cleanupObservers();
            this._handlePaginationElements();
        }
    }

    /**
     * Show the loading spinner.
     * @returns {void}
     * @private
     */
    _showLoading() {
        showLoadingSpinner({
            shadowRoot: this.shadowRoot,
            unauthorized: this._unauthorized,
            isDetailsView: this._currentView === ROUTES.DETAILS,
        });
    }

    /**
     * Remove the content from the grid.
     * @returns {void}
     * @private
     */
    _removeContent() {
        if (this._container) {
            this._container.innerHTML = '';
        }

        const paginationConfigValue = getPaginationConfig();
        if (!paginationConfigValue) {
            scrollToTop();
        }
    }

    /**
     * Handle the pagination elements.
     * @returns {void}
     * @private
     */
    _handlePaginationElements() {
        if (
            !this._paginationValue ||
            !this._leftButton ||
            !this._rightButton ||
            !this._doubleLeftButton ||
            !this._doubleRightButton
        ) {
            return;
        }

        this._doubleLeftButton.setAttribute('disabled', '');
        this._leftButton.setAttribute('disabled', '');

        this._rightButton.removeAttribute('disabled');
        this._doubleRightButton.setAttribute('disabled', '');

        this._paginationValue.textContent = this._currentPage + 1;

        if (this._currentPage) {
            this._leftButton.removeAttribute('disabled');

            if (this._currentPage > 1) {
                this._doubleLeftButton.removeAttribute('disabled');
            }
        } else {
            this._leftButton.setAttribute('disabled', '');
            this._doubleLeftButton.setAttribute('disabled', '');
        }

        if (this._totalPagination === this._currentPage + 1) {
            this._rightButton.setAttribute('disabled', '');
            this._doubleRightButton.setAttribute('disabled', '');
        } else {
            this._rightButton.removeAttribute('disabled');
            this._doubleRightButton.removeAttribute('disabled');
        }

        this._handleTotalPaginationAndDisabledButtons();

        if (this._firstRender) {
            this._firstRender = false;
            this._addPaginationEventListeners();
        }
    }

    /**
     * Add pagination event listeners.
     * @returns {void}
     * @private
     */
    _addPaginationEventListeners() {
        if (
            !this._leftButton ||
            !this._rightButton ||
            !this._doubleLeftButton ||
            !this._doubleRightButton ||
            !this._paginationValue
        ) {
            return;
        }

        this._leftButton.addEventListener('click', this._handlePaginationLeftClickBound);
        this._rightButton.addEventListener(
            'click',
            this._handlePaginationRightClickBound,
        );
        this._doubleLeftButton.addEventListener(
            'click',
            this._handlePaginationDoubleLeftClickBound,
        );
        this._doubleRightButton.addEventListener(
            'click',
            this._handlePaginationDoubleRightClickBound,
        );
    }

    /**
     * Handle pagination left button click.
     * @returns {void}
     * @private
     */
    _handlePaginationLeftClick() {
        if (this._isLoading) return;

        this._handleTriggerShowLoadingComponent();
        if (this._paginationValue) {
            this._paginationValue.textContent = this._currentPage;
        }
        this._removeContent();

        if (this._currentPage === 1) {
            if (this._doubleLeftButton)
                this._doubleLeftButton.setAttribute('disabled', '');
            if (this._leftButton) this._leftButton.setAttribute('disabled', '');
        }

        fetchLessContent(this._currentPage, this._searchReportsResults);
        scrollToTop();
    }

    /**
     * Handle pagination right button click.
     * @returns {void}
     * @private
     */
    _handlePaginationRightClick() {
        if (this._isLoading) return;

        this._handleTriggerShowLoadingComponent();
        if (this._paginationValue) {
            this._paginationValue.textContent = this._currentPage + 2;
        }
        this._removeContent();

        if (this._doubleLeftButton) this._doubleLeftButton.removeAttribute('disabled');

        fetchMoreContent(this._currentPage, this._searchReportsResults);
        scrollToTop();
    }

    /**
     * Handle pagination double left button click.
     * @returns {void}
     * @private
     */
    _handlePaginationDoubleLeftClick() {
        if (this._isLoading) return;

        this._handleTriggerShowLoadingComponent();
        if (this._paginationValue) {
            this._paginationValue.textContent = 1;
        }
        this._removeContent();

        if (this._doubleLeftButton) this._doubleLeftButton.setAttribute('disabled', '');
        if (this._leftButton) this._leftButton.setAttribute('disabled', '');

        this._currentPage = 1;
        fetchLessContent(this._currentPage, this._searchReportsResults);
        scrollToTop();
    }

    /**
     * Handle pagination double right button click.
     * @returns {void}
     * @private
     */
    _handlePaginationDoubleRightClick() {
        if (this._isLoading) return;

        this._handleTriggerShowLoadingComponent();
        if (this._paginationValue) {
            this._paginationValue.textContent = this._totalPagination;
        }
        this._removeContent();

        this._currentPage = this._totalPagination - 2;
        fetchMoreContent(this._currentPage, this._searchReportsResults);
        scrollToTop();
    }

    /**
     * Handle the total pagination and disabled buttons.
     * @returns {void}
     * @private
     */
    _handleTotalPaginationAndDisabledButtons() {
        this._totalPagination = calculateTotalPagination(this._totalElements);

        if (
            this._totalPagination <= 1 &&
            !this._avoidRightPaginationButtons.includes(this._currentView)
        ) {
            if (this._rightButton) this._rightButton.setAttribute('disabled', '');
            if (this._doubleRightButton)
                this._doubleRightButton.setAttribute('disabled', '');
        }

        if (this._totalPagination > 1 && this._firstRender) {
            if (this._rightButton) this._rightButton.removeAttribute('disabled');
            if (this._doubleRightButton)
                this._doubleRightButton.removeAttribute('disabled');
        }

        if (
            this._avoidRightPaginationButtons.includes(this._currentView) &&
            this._container &&
            this._container.childElementCount < getPerPageConfig()
        ) {
            if (this._rightButton) this._rightButton.setAttribute('disabled', '');
            if (this._doubleRightButton)
                this._doubleRightButton.setAttribute('disabled', '');
        }
    }

    /**
     * Move to the selected element.
     * @returns {void}
     * @private
     */
    _moveToSelectedElement() {
        const currentFiltersStateHistory = getCurrentSearcherObj();

        if (!currentFiltersStateHistory) return;

        this._lastSelectedElementId =
            currentFiltersStateHistory[STORE_SEARCHER_FILTERS.LAST_ELEMENT_SELECTED_ID];

        if (!this._lastSelectedElementId) return;

        this._moveToSelectedElementTimeout = setTimeout(() => {
            const selectedElement = getSelectedElement(
                this._container,
                this._lastSelectedElementId,
                this._currentView,
                this._searchImgsResults,
            );

            if (!selectedElement) return;

            const selectedElementContainer = selectedElement.shadowRoot?.querySelector(
                '[data-main-card-container]',
            );
            if (!selectedElementContainer) return;

            selectedElementContainer.classList.add('border-container');
            selectedElementContainer.classList.add('animate-border');

            setTimeout(() => {
                selectedElementContainer.classList.remove('border-container');
                selectedElementContainer.classList.remove('animate-border');
            }, 10000);

            scrollToElement(selectedElementContainer);
        }, 1000);
    }

    /**
     * Render the previous grid elements.
     * @param {Array} data - The data to render.
     * @returns {void}
     * @private
     */
    _renderPreviousGrid(data) {
        const currentFirstElement = this._container?.firstElementChild;

        if (this._loadPreviousElementsButton) {
            this._loadPreviousElementsButton.innerHTML =
                this._loadPreviousElementsButtonText;
            if (!this._loadPreviousElements) {
                this._loadPreviousElementsButton.style.display = 'none';
            }
        }

        renderGridElements(
            data,
            this._container,
            this._currentView,
            this._currentReportType,
            this.getAttribute(RESULTS_GRID_LAYOUT_ATTRIBUTES.TITLE),
            this._searchImgsResults,
            MIN_ACTIVES_HEIGHT,
            currentFirstElement,
        );

        this._scrollToElementTimeout = setTimeout(() => {
            if (currentFirstElement) {
                const mainCardContainer = currentFirstElement.shadowRoot?.querySelector(
                    '[data-main-card-container]',
                );
                if (mainCardContainer) {
                    scrollToElement(mainCardContainer);
                }
            }
        }, 1000);
    }
}

customElements.define(RESULTS_GRID_LAYOUT_WEB_COMPONENT, ResultsGridLayout);
